import { LitElement, html, css, PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { resolveRadius } from "../helper/utils/size-resolver";
import { resolveColor } from "../helper/utils/color-resolver";
import { PresetController } from "../helper/controllers/preset-controller";

export type SkyCardElevation = "none" | "sm" | "md" | "lg";
export type SkyCardSectionLayout = "inline" | "stack";
export type SkyCardSectionTarget = "both" | "header" | "footer" | "none";

/**
 * @element sky-card
 *
 * @summary Customizable card with configurable header/footer sections, blur/background styles, elevation, insets, overlays, and separators.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/card
 *
 * @slot - Default slot for the card body content (replaces `bodyPadding` property if used).
 * @slot title - Slot for the card title.
 * @slot subtitle - Slot for the card subtitle.
 * @slot actions - Slot for the card actions (usually buttons).
 * @slot footer-title - Slot for the footer title.
 * @slot footer-subtitle - Slot for the footer subtitle.
 * @slot footer-actions - Slot for the footer actions (usually buttons).
 *
 * @csspart card - The main card container element.
 * @csspart header - The card header section.
 * @csspart footer - The card footer section.
 * @csspart ripple - The ripple animation element injected when the card is clicked.
 *
 * @property {string} bg - Background color of the card. Accepts color tokens or custom CSS colors. Default: `"primary"`.
 * @property {string} blurLevel - Level of blur applied to the background. Default: `"none"`.
 * @property {string} radius - Border radius of the card. Default: `"md"`.
 * @property {string} bodyPadding - Padding for the card body. Default: `"none"`.
 * @property {SkyCardElevation} elevation - Shadow depth of the card. Default: `"md"`.
 * @property {boolean} outlined - Whether the card has an outline. Default: `false`.
 * @property {boolean} clickable - Whether the card is clickable. Default: `false`.
 * @property {string} headerBg - Background color of the header. Default: `"transparent"`.
 * @property {string} headerBlur - Blur level for the header background. Default: `"none"`.
 * @property {string} headerPadding - Padding for the header. Default: `"md"`.
 * @property {SkyCardSectionLayout} headerLayout - Layout of the header content. Default: `"inline"`.
 * @property {string} headerInsetGap - Inset gap for the header section. Default: `"sm"`.
 * @property {string} headerRadius - Border radius for the header. Default: `"md"`.
 * @property {string} headerSeparatorColor - Color for the header separator. Default: `"border-light"`.
 * @property {string} footerBg - Background color of the footer. Default: `"transparent"`.
 * @property {string} footerBlur - Blur level for the footer background. Default: `"none"`.
 * @property {string} footerPadding - Padding for the footer. Default: `"md"`.
 * @property {SkyCardSectionLayout} footerLayout - Layout of the footer content. Default: `"inline"`.
 * @property {string} footerInsetGap - Inset gap for the footer section. Default: `"sm"`.
 * @property {string} footerRadius - Border radius for the footer. Default: `"md"`.
 * @property {string} footerSeparatorColor - Color for the footer separator. Default: `"border-light"`.
 * @property {number} padRatio - Ratio to adjust padding. Default: `1`.
 * @property {SkyCardSectionTarget} separators - Determines where separators should appear. Default: `"none"`.
 * @property {SkyCardSectionTarget} overlays - Determines where overlays should appear. Default: `"none"`.
 * @property {SkyCardSectionTarget} insets - Determines where insets should appear. Default: `"none"`.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @method refreshLayout Recomputes card variables and section synchronization.
 *
 * @example
 * ```html
 * <sky-card bg="primary" elevation="lg" headerpadding="md">
 *   <span slot="title">Monthly Report</span>
 *   <span slot="subtitle">April 2026</span>
 *   <div>Total revenue increased by 18%.</div>
 *   <button slot="actions">View Details</button>
 * </sky-card>
 * ```
 * ```vue
 * <template>
 *   <sky-card bg="primary" elevation="lg" headerpadding="md">
 *     <span slot="title">Monthly Report</span>
 *     <span slot="subtitle">April 2026</span>
 *     <div>Total revenue increased by 18%.</div>
 *     <button slot="actions">View Details</button>
 *   </sky-card>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-card bg="primary" elevation="lg" headerpadding="md">
 *       <span slot="title">Monthly Report</span>
 *       <span slot="subtitle">April 2026</span>
 *       <div>Total revenue increased by 18%.</div>
 *       <button slot="actions">View Details</button>
 *     </sky-card>
 *   );
 * }
 * ```
 */
@customElement("sky-card")
export class SkyCard extends LitElement {
  @property({ type: String, reflect: true }) bg = "primary";
  @property({ type: String, reflect: true }) blurLevel = "none";
  @property({ type: String, reflect: true }) radius = "md";
  @property({ type: String, reflect: true }) bodyPadding = "none";
  @property({ type: String, reflect: true }) elevation: SkyCardElevation = "md";
  @property({ type: Boolean, reflect: true }) outlined = false;
  @property({ type: Boolean, reflect: true }) clickable = false;

  @property({ type: String, reflect: true }) headerBg =
    "transparent";
  @property({ type: String, reflect: true })
  headerBlur = "none";
  @property({ type: String, reflect: true })
  headerPadding = "md";
  @property({ type: String }) headerLayout: SkyCardSectionLayout = "inline";
  @property({ type: String, reflect: true })
  headerInsetGap = "sm";
  @property({ type: String, reflect: true })
  headerRadius = "md";
  @property({ type: String })
  headerSeparatorColor = "border-light";

  @property({ type: String, reflect: true }) footerBg =
    "transparent";
  @property({ type: String, reflect: true })
  footerBlur = "none";
  @property({ type: String, reflect: true })
  footerPadding = "md";
  @property({ type: String }) footerLayout: SkyCardSectionLayout = "inline";
  @property({ type: String, reflect: true })
  footerInsetGap = "sm";
  @property({ type: String, reflect: true })
  footerRadius = "md";
  @property({ type: String })
  footerSeparatorColor = "border-light";

  @property({ type: Number }) padRatio = 1;

  @property({ type: String }) separators: SkyCardSectionTarget = "none";
  @property({ type: String }) overlays: SkyCardSectionTarget = "none";
  @property({ type: String }) insets: SkyCardSectionTarget = "none";

  private _effHeaderSeparator = false;
  private _effFooterSeparator = false;
  private _effHeaderOverlay = false;
  private _effFooterOverlay = false;
  private _effHeaderInset = false;
  private _effFooterInset = false;
  private _varsCache: Record<string, string> = {};

  private static readonly BG_MAP = {
    primary: "var(--sky-glass-primary)",
    secondary: "var(--sky-glass-secondary)",
    tertiary: "var(--sky-glass-tertiary)",
    transparent: "transparent",
  } as const;

  private static readonly BLUR_MAP = {
    none: "none",
    xs: "blur(2px)",
    sm: "var(--sky-blur-secondary)",
    md: "var(--sky-blur-secondary)",
    lg: "var(--sky-blur-tertiary)",
  } as const;

  private static readonly SIZE_MAP = {
    none: "0",
    sm: "var(--sky-space-1\\.5, 6px)",
    md: "var(--sky-space-4, 16px)",
    lg: "var(--sky-space-6, 24px)",
    xl: "var(--sky-space-8, 32px)",
  } as const;

  private static readonly GLASS_BG_TIERS = new Set([
    "primary",
    "secondary",
    "tertiary",
  ]);

  /** Non-overlay inset bars use hover tints — overlay sections own their own blur. */
  private static readonly SECTION_INSET_BG_MAP = {
    primary: "var(--sky-hover-primary)",
    secondary: "var(--sky-hover-secondary)",
    tertiary: "var(--sky-hover-tertiary)",
    transparent: "transparent",
  } as const;

  private static _composeBackdrop(blurLevel: string): string {
    const blur =
      SkyCard.BLUR_MAP[blurLevel as keyof typeof SkyCard.BLUR_MAP] ?? blurLevel;
    if (blur === "none") return "none";
    return `${blur} var(--sky-saturation-secondary) var(--sky-brightness-secondary)`;
  }

  private static _composeElevation(level: SkyCardElevation | string): string {
    const depthMap: Record<SkyCardElevation, string | null> = {
      none: null,
      sm: "var(--sky-box-shadow-primary)",
      md: "var(--sky-box-shadow-secondary)",
      lg: "var(--sky-box-shadow-tertiary)",
    };
    const depth = depthMap[level as SkyCardElevation];
    if (depth === null) return "none";
    if (depth === undefined) return level;
    return `var(--sky-box-shadow-inset), ${depth}`;
  }


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static styles = css`
    :host {
      display: block;
    }

    .card {
      display: flex;
      flex-direction: column;
      width: 100%;
      position: relative;
      overflow: hidden;
      box-sizing: border-box;
      color: var(--sky-text-primary);
      background: var(--_card-bg, var(--sky-glass-primary));
      background-image: var(--_card-glass-surface, var(--sky-glass-surface));
      border-radius: var(--_card-radius, var(--sky-radius-secondary));
      backdrop-filter: var(--_card-backdrop, none);
      -webkit-backdrop-filter: var(--_card-backdrop, none);
      box-shadow: var(
        --_card-shadow,
        var(--sky-box-shadow-inset),
        var(--sky-box-shadow-primary)
      );
      border: var(--_card-border, var(--sky-border-primary));
      transition: var(--sky-transition-default);
    }

    :host([outlined]) .card {
      box-shadow: none;
      border: var(--sky-border-primary);
    }
    :host([clickable]) .card {
      cursor: pointer;
    }
    :host([clickable]) .card:hover {
      box-shadow: var(
        --_card-shadow-hover,
        var(--sky-box-shadow-inset),
        var(--sky-box-shadow-hover)
      );
    }

    .section {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--sky-space-1\\.5, 6px);
      box-sizing: border-box;
      min-width: 0;
      background: transparent;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
    .section.stack {
      flex-direction: column;
      align-items: stretch;
      gap: var(--sky-space-1\\.5, 6px);
    }
    .section.row {
      flex-direction: row;
    }

    .overlay-top {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1;
    }
    .overlay-bottom {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1;
    }

    .inset {
      overflow: hidden;
      box-shadow: none;
    }
    .section.inset[part="header"] {
      border: var(--_header-inset-border, 0 solid transparent);
      border-radius: var(--_header-radius, var(--sky-radius-secondary));
    }
    .section.inset[part="footer"] {
      border: var(--_footer-inset-border, 0 solid transparent);
      border-radius: var(--_footer-radius, var(--sky-radius-secondary));
    }
    .overlay-top.inset {
      top: var(--_header-inset-gap, var(--sky-space-2, 8px));
      left: var(--_header-inset-gap, var(--sky-space-2, 8px));
      right: var(--_header-inset-gap, var(--sky-space-2, 8px));
    }
    .overlay-bottom.inset {
      bottom: var(--_footer-inset-gap, var(--sky-space-2, 8px));
      left: var(--_footer-inset-gap, var(--sky-space-2, 8px));
      right: var(--_footer-inset-gap, var(--sky-space-2, 8px));
    }

    .section[part="header"]:has(slot:not([empty])),
    .section[part="header"][has-content] {
      background: var(--_header-bg);
      background-image: var(--_header-glass-surface, none);
      padding-block: var(--_header-pad-y, var(--sky-space-3, 12px));
      padding-inline: var(--_header-pad-x, var(--sky-space-4, 16px));
    }
    .section[part="footer"]:has(slot:not([empty])),
    .section[part="footer"][has-content] {
      background: var(--_footer-bg);
      background-image: var(--_footer-glass-surface, none);
      padding-block: var(--_footer-pad-y, var(--sky-space-3, 12px));
      padding-inline: var(--_footer-pad-x, var(--sky-space-4, 16px));
    }

    .section[part="header"].overlay-top:has(slot:not([empty])),
    .section[part="header"].overlay-top[has-content] {
      backdrop-filter: var(--_header-backdrop, none);
      -webkit-backdrop-filter: var(--_header-backdrop, none);
    }
    .section[part="footer"].overlay-bottom:has(slot:not([empty])),
    .section[part="footer"].overlay-bottom[has-content] {
      backdrop-filter: var(--_footer-backdrop, none);
      -webkit-backdrop-filter: var(--_footer-backdrop, none);
    }

    .section[part="header"]:not(.inset):has(slot:not([empty])),
    .section[part="header"]:not(.inset)[has-content] {
      border-bottom: var(--_header-separator, 0px) solid
        var(--_header-separator-color, var(--sky-border-light));
    }
    .section[part="footer"]:not(.inset):has(slot:not([empty])),
    .section[part="footer"]:not(.inset)[has-content] {
      border-top: var(--_footer-separator, 0px) solid
        var(--_footer-separator-color, var(--sky-border-light));
    }

    ::slotted(:not([slot])) {
      box-sizing: border-box;
      padding-block: var(--_body-pad-y, var(--sky-space-3, 12px));
      padding-inline: var(--_body-pad-x, var(--sky-space-4, 16px));
    }
    slot[empty] {
      display: none;
    }

    ::slotted([slot="title"]) {
      font-size: var(--sky-font-size-lg, 18px);
      font-weight: var(--sky-font-weight-semibold, 600);
      margin: 0;
      color: var(--sky-text-primary);
      text-align: start;
      text-shadow: var(--sky-text-shadow-on-glass, none);
    }
    ::slotted([slot="subtitle"]) {
      font-size: var(--sky-font-size-sm, 14px);
      margin: 0;
      color: var(--sky-text-secondary);
      text-align: start;
      text-shadow: var(--sky-text-shadow-on-glass, none);
    }
    ::slotted([slot="actions"]) {
      display: inline-flex;
      gap: var(--sky-space-1\\.5, 6px);
      align-items: center;
      margin-left: auto;
    }
    ::slotted([slot="footer-title"]) {
      font-size: var(--sky-font-size-md, 16px);
      font-weight: var(--sky-font-weight-medium, 500);
      margin: 0;
      color: var(--sky-text-primary);
      text-align: start;
    }
    ::slotted([slot="footer-subtitle"]) {
      font-size: var(--sky-font-size-xs, 12px);
      margin: 0;
      color: var(--sky-text-secondary);
      text-align: start;
    }
    ::slotted([slot="footer-actions"]) {
      display: inline-flex;
      gap: var(--sky-space-1\\.5, 6px);
      align-items: center;
      margin-left: auto;
    }

    .main {
      width: 100%;
    }

    @media (prefers-reduced-motion: reduce) {
      .card {
        transition: none;
      }
    }
  `;

  override firstUpdated(): void {
    this._wireSlotChangeHandlers();

    this._syncHeader();
    this._syncFooter();
  }

  /**
   * After PresetController hostUpdate — card chrome may come from presets.
   * Keep flag recompute + CSS var apply here (not willUpdate).
   */
  protected override update(changed: PropertyValues): void {
    if (
      changed.has("separators") ||
      changed.has("overlays") ||
      changed.has("insets")
    ) {
      this._computeEffectiveFlags();
    }

    if (
      changed.has("bg") ||
      changed.has("blurLevel") ||
      changed.has("radius") ||
      changed.has("elevation") ||
      changed.has("preset")
    ) {
      this._applyCardVars();
    }
    if (
      changed.has("headerPadding") ||
      changed.has("bodyPadding") ||
      changed.has("footerPadding") ||
      changed.has("padRatio") ||
      changed.has("preset")
    ) {
      this._applyPaddingVars();
    }
    if (
      changed.has("headerBg") ||
      changed.has("footerBg") ||
      changed.has("headerBlur") ||
      changed.has("footerBlur") ||
      changed.has("headerRadius") ||
      changed.has("footerRadius") ||
      changed.has("headerInsetGap") ||
      changed.has("footerInsetGap") ||
      changed.has("headerSeparatorColor") ||
      changed.has("footerSeparatorColor") ||
      changed.has("separators") ||
      changed.has("insets") ||
      changed.has("overlays") ||
      changed.has("preset")
    ) {
      this._applySectionChrome();
    }
    super.update(changed);
  }

  private _wireSlotChangeHandlers(): void {
    this.renderRoot
      .querySelector("#slot-title")
      ?.addEventListener("slotchange", () => this._syncHeader());
    this.renderRoot
      .querySelector("#slot-subtitle")
      ?.addEventListener("slotchange", () => this._syncHeader());
    this.renderRoot
      .querySelector("#slot-actions")
      ?.addEventListener("slotchange", () => this._syncHeader());

    this.renderRoot
      .querySelector("#slot-f-title")
      ?.addEventListener("slotchange", () => this._syncFooter());
    this.renderRoot
      .querySelector("#slot-f-subtitle")
      ?.addEventListener("slotchange", () => this._syncFooter());
    this.renderRoot
      .querySelector("#slot-f-actions")
      ?.addEventListener("slotchange", () => this._syncFooter());
  }

  private _syncHeader(): void {
    const hdr = this.renderRoot.querySelector("#hdr");
    const slots = [
      this.renderRoot.querySelector("#slot-title") as HTMLSlotElement | null,
      this.renderRoot.querySelector("#slot-subtitle") as HTMLSlotElement | null,
      this.renderRoot.querySelector("#slot-actions") as HTMLSlotElement | null,
    ].filter(Boolean) as HTMLSlotElement[];

    const has = slots.some(
      (s) => s.assignedNodes({ flatten: true }).length > 0
    );
    hdr?.toggleAttribute("has-content", has);

    slots.forEach((s) =>
      s.toggleAttribute(
        "empty",
        s.assignedNodes({ flatten: true }).length === 0
      )
    );
  }

  private _syncFooter(): void {
    const ftr = this.renderRoot.querySelector("#ftr");
    const slots = [
      this.renderRoot.querySelector("#slot-f-title") as HTMLSlotElement | null,
      this.renderRoot.querySelector(
        "#slot-f-subtitle"
      ) as HTMLSlotElement | null,
      this.renderRoot.querySelector(
        "#slot-f-actions"
      ) as HTMLSlotElement | null,
    ].filter(Boolean) as HTMLSlotElement[];

    const has = slots.some(
      (s) => s.assignedNodes({ flatten: true }).length > 0
    );
    ftr?.toggleAttribute("has-content", has);
    slots.forEach((s) =>
      s.toggleAttribute(
        "empty",
        s.assignedNodes({ flatten: true }).length === 0
      )
    );
  }

  private _expandPair(sh: SkyCardSectionTarget): [boolean, boolean] {
    switch (sh) {
      case "both":
        return [true, true];
      case "header":
        return [true, false];
      case "footer":
        return [false, true];
      case "none":
      default:
        return [false, false];
    }
  }

  private _computeEffectiveFlags(): void {
    [this._effHeaderSeparator, this._effFooterSeparator] = this._expandPair(
      this.separators
    );
    [this._effHeaderOverlay, this._effFooterOverlay] = this._expandPair(
      this.overlays
    );
    [this._effHeaderInset, this._effFooterInset] = this._expandPair(
      this.insets
    );
  }

  private _setVar(name: string, value: string): void {
    if (this._varsCache[name] === value) return;
    this.style.setProperty(name, value);
    this._varsCache[name] = value;
  }

  private _resolveSectionBg(
    token: string,
    isInset: boolean,
    isOverlay: boolean
  ): string {
    if (isInset && !isOverlay) {
      return (
        SkyCard.SECTION_INSET_BG_MAP[
          token as keyof typeof SkyCard.SECTION_INSET_BG_MAP
        ] ?? token
      );
    }
    return SkyCard.BG_MAP[token as keyof typeof SkyCard.BG_MAP] ?? token;
  }

  private _resolveSectionGlassSurface(token: string): string {
    return SkyCard.GLASS_BG_TIERS.has(token)
      ? "var(--sky-glass-surface)"
      : "none";
  }

  private _applyCardVars(): void {
    this._setVar(
      "--_card-bg",
      SkyCard.BG_MAP[this.bg as keyof typeof SkyCard.BG_MAP] ?? this.bg
    );
    this._setVar(
      "--_card-glass-surface",
      SkyCard.GLASS_BG_TIERS.has(this.bg)
        ? "var(--sky-glass-surface)"
        : "none"
    );

    this._setVar(
      "--_card-backdrop",
      SkyCard._composeBackdrop(this.blurLevel)
    );

    this._setVar("--_card-radius", resolveRadius(this.radius));
    this._setVar(
      "--_card-shadow",
      SkyCard._composeElevation(this.elevation)
    );
    this._setVar(
      "--_card-shadow-hover",
      "var(--sky-box-shadow-inset), var(--sky-box-shadow-hover)"
    );
  }

  private _applyPaddingVars(): void {
    const h =
      SkyCard.SIZE_MAP[this.headerPadding as keyof typeof SkyCard.SIZE_MAP] ??
      this.headerPadding;
    const b =
      SkyCard.SIZE_MAP[this.bodyPadding as keyof typeof SkyCard.SIZE_MAP] ??
      this.bodyPadding;
    const f =
      SkyCard.SIZE_MAP[this.footerPadding as keyof typeof SkyCard.SIZE_MAP] ??
      this.footerPadding;

    const ratio = Number.isFinite(this.padRatio)
      ? Math.max(0, this.padRatio)
      : 1;

    this._setVar("--_header-pad-y", h);
    this._setVar("--_header-pad-x", `calc(${h} * ${ratio})`);

    this._setVar("--_body-pad-y", b);
    this._setVar("--_body-pad-x", `calc(${b} * ${ratio})`);

    this._setVar("--_footer-pad-y", f);
    this._setVar("--_footer-pad-x", `calc(${f} * ${ratio})`);
  }

  private _applySectionChrome(): void {
    const headerSep = this._effHeaderInset
      ? "0px"
      : this._effHeaderSeparator
        ? "1px"
        : "0px";
    const footerSep = this._effFooterInset
      ? "0px"
      : this._effFooterSeparator
        ? "1px"
        : "0px";
    this._setVar("--_header-separator", headerSep);
    this._setVar("--_footer-separator", footerSep);

    this._setVar("--_header-separator-color", resolveColor(this.headerSeparatorColor));
    this._setVar("--_footer-separator-color", resolveColor(this.footerSeparatorColor));

    const headerInsetBorder =
      this._effHeaderInset && this._effHeaderSeparator
        ? "1px solid var(--sky-border-light)"
        : "0 solid transparent";
    const footerInsetBorder =
      this._effFooterInset && this._effFooterSeparator
        ? "1px solid var(--sky-border-light)"
        : "0 solid transparent";
    this._setVar("--_header-inset-border", headerInsetBorder);
    this._setVar("--_footer-inset-border", footerInsetBorder);

    this._setVar(
      "--_header-bg",
      this._resolveSectionBg(
        this.headerBg,
        this._effHeaderInset,
        this._effHeaderOverlay
      )
    );
    this._setVar(
      "--_footer-bg",
      this._resolveSectionBg(
        this.footerBg,
        this._effFooterInset,
        this._effFooterOverlay
      )
    );
    this._setVar(
      "--_header-glass-surface",
      this._effHeaderOverlay
        ? this._resolveSectionGlassSurface(this.headerBg)
        : "none"
    );
    this._setVar(
      "--_footer-glass-surface",
      this._effFooterOverlay
        ? this._resolveSectionGlassSurface(this.footerBg)
        : "none"
    );
    this._setVar(
      "--_header-backdrop",
      this._effHeaderOverlay
        ? SkyCard._composeBackdrop(this.headerBlur)
        : "none"
    );
    this._setVar(
      "--_footer-backdrop",
      this._effFooterOverlay
        ? SkyCard._composeBackdrop(this.footerBlur)
        : "none"
    );

    this._setVar(
      "--_header-radius",
      resolveRadius(this.headerRadius)
    );
    this._setVar(
      "--_footer-radius",
      resolveRadius(this.footerRadius)
    );
    this._setVar(
      "--_header-inset-gap",
      SkyCard.SIZE_MAP[this.headerInsetGap as keyof typeof SkyCard.SIZE_MAP] ??
      this.headerInsetGap
    );
    this._setVar(
      "--_footer-inset-gap",
      SkyCard.SIZE_MAP[this.footerInsetGap as keyof typeof SkyCard.SIZE_MAP] ??
      this.footerInsetGap
    );
  }
  /**
   * Recomputes card variables and section synchronization.
   *
   * @returns {void}
   */
  public refreshLayout(): void {
    this._computeEffectiveFlags();
    this._applyCardVars();
    this._applyPaddingVars();
    this._applySectionChrome();
    this._syncHeader();
    this._syncFooter();
  }

  override render() {
    const headerLayoutClass = this.headerLayout === "inline" ? "row" : "stack";
    const footerLayoutClass = this.footerLayout === "inline" ? "row" : "stack";
    const headerClasses = classMap({
      section: true,
      [headerLayoutClass]: true,
      "overlay-top": !!this._effHeaderOverlay,
      inset: !!this._effHeaderInset,
    });
    const footerClasses = classMap({
      section: true,
      [footerLayoutClass]: true,
      "overlay-bottom": !!this._effFooterOverlay,
      inset: !!this._effFooterInset,
    });

    return html`
      <div class="card" part="card">
        <div class=${headerClasses} part="header" id="hdr">
          <div class="main">
            <slot name="title" id="slot-title"></slot>
            <slot name="subtitle" id="slot-subtitle"></slot>
          </div>
          <slot name="actions" id="slot-actions"></slot>
        </div>

        <slot></slot>

        <div class=${footerClasses} part="footer" id="ftr">
          <div class="main">
            <slot name="footer-title" id="slot-f-title"></slot>
            <slot name="footer-subtitle" id="slot-f-subtitle"></slot>
          </div>
          <slot name="footer-actions" id="slot-f-actions"></slot>
        </div>
      </div>
    `;
  }
}
